/**
 * Version checker: prompts a refresh only when a NEW FRONTEND BUILD is live.
 * Compares the build id baked into this bundle (REACT_APP_BUILD_ID) with
 * /version.json from the server; identical across all servers, so backend
 * restarts or multiple instances never trigger it. Shown once per build, and
 * held back while a workout/timer is on screen.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const POLL_MS = 10 * 60 * 1000;
const BUILD_ID = process.env.REACT_APP_BUILD_ID;
const BUSY_PATHS = ['/workout/', '/live-workout/', '/timer'];
const SEEN_KEY = 'version-prompted-for';

async function fetchLatestBuild() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json())?.version || null;
  } catch {
    return null;
  }
}

async function nukeCachesAndReload() {
  try {
    if (window.caches) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch { /* noop */ }
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  window.location.replace(url.toString());
}

export default function VersionChecker() {
  const location = useLocation();
  const pendingRef = useRef(null);
  const busy = BUSY_PATHS.some((p) => location.pathname.startsWith(p));

  const prompt = (latest) => {
    if (sessionStorage.getItem(SEEN_KEY) === latest) return;
    sessionStorage.setItem(SEEN_KEY, latest);
    toast.info('A new version is available', {
      duration: Infinity,
      description: 'Refresh to load the latest features and fixes.',
      action: { label: 'Refresh', onClick: () => nukeCachesAndReload() },
    });
  };

  useEffect(() => {
    if (!BUILD_ID) return undefined; // dev server: nothing to compare
    let cancelled = false;
    const check = async () => {
      const latest = await fetchLatestBuild();
      if (cancelled || !latest || latest === BUILD_ID) return;
      pendingRef.current = latest;
      if (!busy) prompt(latest);
    };
    check();
    const t = setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [busy]);

  return null;
}
